/**
 * Node Search Results Component
 * Displays search results with multi-select checkboxes and descendant options
 */

import React from 'react';
import type { FC } from 'react';
import styled from 'styled-components';
import type { NodeTypeDto, NodeSearchResultsProps } from '../types';
import { getNodeTypeColor, getNodeTypeName } from '../utils';

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SearchHeader = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.5rem;
  background: ${props => props.theme?.inputBgd || '#161b22'};
  color: ${props => props.theme?.textColor || '#e6edf3'};
  border: 1px solid ${props => props.theme?.borderColor || '#30363d'};
  border-radius: 4px;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: ${props => props.theme?.primaryColor || '#58a6ff'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SearchButton = styled.button`
  padding: 0.5rem 1rem;
  background: ${props => props.theme?.buttonBgd || '#238636'};
  color: #ffffff;
  border: none;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: ${props => props.theme?.buttonBgdHover || '#2ea043'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ResultsHeader = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme?.labelColor || '#8b949e'};
  padding: 0.5rem 0;
`;

const ResultsContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid ${props => props.theme?.borderColor || '#30363d'};
  border-radius: 4px;
`;

const ResultRow = styled.div.withConfig({
  shouldForwardProp: prop => (prop as string) !== 'selected',
})<{ selected?: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  gap: 0.75rem;
  padding: 0.75rem;
  align-items: center;
  border-bottom: 1px solid ${props => props.theme?.borderColor || '#30363d'};
  background: ${props => (props.selected ? 'rgba(88, 166, 255, 0.1)' : 'transparent')};
  transition: background 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${props => props.theme?.panelBackgroundHover || 'rgba(255, 255, 255, 0.05)'};
  }
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const NodeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const NodeTypeBadge = styled.span.withConfig({
  shouldForwardProp: prop => (prop as string) !== 'nodeTypeColor',
})<{ nodeTypeColor: string }>`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: ${props => props.nodeTypeColor}33;
  color: ${props => props.nodeTypeColor};
  border: 1px solid ${props => props.nodeTypeColor}66;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  font-family: monospace;
`;

const NodeName = styled.span`
  font-size: 0.875rem;
  color: ${props => props.theme?.textColor || '#e6edf3'};
`;

const DescendantOptions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
`;

const NodeTypeSelect = styled.select`
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  background-color: ${props => props.theme?.panelBackground || '#29323c'};
  color: ${props => props.theme?.textColor || '#ffffff'};
  border: 1px solid ${props => props.theme?.inputBorderColor || '#3a414c'};
  border-radius: 4px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  option {
    background-color: ${props => props.theme?.panelBackground || '#29323c'};
    color: ${props => props.theme?.textColor || '#ffffff'};
  }
`;

const SmallCheckbox = styled(Checkbox)`
  width: 14px;
  height: 14px;
`;

const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${props => props.theme?.labelColor || '#8b949e'};
  font-size: 0.875rem;
`;

// ============================================================================
// Component
// ============================================================================

export const NodeSearchResults: FC<NodeSearchResultsProps & { nodeTypes?: NodeTypeDto[] }> = ({
  results,
  selectedLsids,
  onToggleSelection,
  onToggleDescendants,
  onDescendantTypeChange,
  descendantStates,
  isLoading,
  nodeTypes,
  searchQuery,
  onSearchQueryChange,
  onSearch,
}) => {
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearch?.();
    }
  };

  return (
    <Container>
      <SearchHeader>
        <SearchInput
          type='text'
          value={searchQuery || ''}
          onChange={e => onSearchQueryChange?.(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder='Search by node name...'
          disabled={isLoading}
          aria-label='Search nodes'
          data-testid='loadseer-search-input'
        />
        <SearchButton
          onClick={onSearch}
          disabled={isLoading || !searchQuery}
          aria-label='Search'
          data-testid='loadseer-search-button'
        >
          {isLoading ? 'Searching...' : 'Search'}
        </SearchButton>
      </SearchHeader>

      {results.length > 0 && (
        <ResultsHeader>
          {results.length} result{results.length !== 1 ? 's' : ''} found
        </ResultsHeader>
      )}

      <ResultsContainer>
        {results.length === 0 ? (
          <EmptyState>
            {isLoading ? 'Searching...' : 'No results. Enter a search term and click Search.'}
          </EmptyState>
        ) : (
          results.map(node => {
            const isSelected = selectedLsids.has(node.lsid);
            const descendantState = descendantStates.get(node.lsid);
            const nodeTypeColor = getNodeTypeColor(node.nodeType);
            const nodeTypeName = getNodeTypeName(node.nodeType, nodeTypes);

            return (
              <ResultRow key={node.lsid} selected={isSelected}>
                <Checkbox
                  checked={isSelected}
                  onChange={() => onToggleSelection(node)}
                  aria-label={`Select ${node.name}`}
                />

                <NodeInfo>
                  <NodeTypeBadge nodeTypeColor={nodeTypeColor} title={nodeTypeName}>
                    {node.nodeType}
                  </NodeTypeBadge>
                  <NodeName>{node.name}</NodeName>
                </NodeInfo>

                <DescendantOptions>
                  <SmallCheckbox
                    checked={descendantState?.enabled || false}
                    onChange={e => onToggleDescendants(node.lsid, e.target.checked)}
                    disabled={!isSelected}
                    aria-label={`Include descendants for ${node.name}`}
                  />
                  <label
                    style={{
                      cursor: isSelected ? 'pointer' : 'not-allowed',
                      opacity: isSelected ? 1 : 0.5,
                    }}
                  >
                    Include descendants
                  </label>
                  {descendantState?.enabled && isSelected && (
                    <NodeTypeSelect
                      value={descendantState.nodeType || ''}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        onDescendantTypeChange?.(node.lsid, e.target.value || undefined)
                      }
                      aria-label={`Filter descendants of ${node.name} by type`}
                      title='Filter descendants by node type'
                    >
                      <option value=''>All types</option>
                      {nodeTypes?.map(nt => (
                        <option key={nt.code} value={nt.code}>
                          {nt.name}
                        </option>
                      ))}
                    </NodeTypeSelect>
                  )}
                </DescendantOptions>

                <div style={{ minWidth: 20 }} />
              </ResultRow>
            );
          })
        )}
      </ResultsContainer>
    </Container>
  );
};
